import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, WifiOff } from "lucide-react";
import { PropsWithChildren, useEffect } from "react";
import { Throbber } from "@lytra/frontend-common";

interface FallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

const STALE_CHUNK_RELOAD_KEY = "lytra:stale-chunk-reloaded-at";
const STALE_CHUNK_RELOAD_WINDOW_MS = 60_000;

/**
 * Render an error of unknown shape as text. Never throws — this component is
 * already the last line of defence, so a failure here leaves a blank page.
 */
function describeError(error: unknown): string | null {
  if (error == null) return null;
  if (error instanceof Error) return `${error.message}\n${error.stack ?? ""}`;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return "Fehler konnte nicht dargestellt werden";
  }
}

function isConnectionError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes("Failed to fetch") ||
    error.message.includes("Network Error") ||
    error.message.includes("NetworkError") ||
    !window.navigator.onLine
  );
}

function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message || "";
  const name = error.name || "";
  return (
    name === "ChunkLoadError" ||
    msg.includes("Importing a module script failed") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(msg)
  );
}

function recentlyReloadedForStaleChunk(): boolean {
  try {
    const raw = sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < STALE_CHUNK_RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
}

function markStaleChunkReload(): void {
  try {
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

function FallbackComponent({ error, resetError, eventId }: FallbackProps) {
  const isOffline = isConnectionError(error);
  const isStale = isStaleChunkError(error);
  const errorDetails = describeError(error);
  const shouldAutoReload = isStale && !recentlyReloadedForStaleChunk();

  useEffect(() => {
    if (!shouldAutoReload) return;
    markStaleChunkReload();
    Sentry.addBreadcrumb({
      category: "app",
      level: "info",
      message: "Stale chunk detected, auto-reloading",
    });
    const t = window.setTimeout(() => window.location.reload(), 150);
    return () => window.clearTimeout(t);
  }, [shouldAutoReload]);

  if (shouldAutoReload) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="mb-4">lytra</div>
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Throbber className="h-4 w-4" />
          Neue Version wird geladen...
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <div className="mb-8">lytra</div>
      <Alert variant="destructive" className="max-w-xl shadow-lg">
        {isOffline ? (
          <WifiOff className="h-5 w-5 mt-1" />
        ) : (
          <AlertTriangle className="h-5 w-5 mt-1" />
        )}
        <AlertTitle className="text-lg font-semibold">
          {isOffline ? "Verbindungsproblem" : "Etwas ist schiefgelaufen"}
        </AlertTitle>
        <AlertDescription>
          <div className="mt-2">
            <p className="text-sm text-foreground">
              {isOffline
                ? "Es scheint ein Problem mit Ihrer Internetverbindung zu geben. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut."
                : "Ein unerwarteter Fehler ist aufgetreten. Unsere Entwickler wurden automatisch informiert und arbeiten bereits an einer Lösung."}
            </p>
            {import.meta.env.DEV && errorDetails && (
              <pre className="mt-3 p-2 bg-muted rounded text-xs overflow-auto max-h-40 whitespace-pre-wrap">
                {errorDetails}
              </pre>
            )}
            <div className="mt-3 text-sm text-muted-foreground">
              <p>Fehler-ID: {eventId}</p>
              <p className="mt-1">
                Wenn Sie den Support kontaktieren möchten, nennen Sie bitte die
                Fehler-ID.
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => resetError()}
                variant="default"
                className="hover:shadow-md transition-shadow"
              >
                {isOffline ? "Erneut versuchen" : "Seite neu laden"}
              </Button>
              <Button
                variant="outline"
                onClick={() => (window.location.href = "/")}
                className="hover:shadow-md transition-shadow text-foreground"
              >
                Zur Startseite
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

export function SentryErrorBoundary({ children }: PropsWithChildren) {
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (!isStaleChunkError(event.reason)) return;
      if (recentlyReloadedForStaleChunk()) return;
      markStaleChunkReload();
      Sentry.addBreadcrumb({
        category: "app",
        level: "info",
        message: "Stale chunk detected (unhandledrejection), auto-reloading",
      });
      window.location.reload();
    };
    window.addEventListener("unhandledrejection", handleRejection);
    return () =>
      window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return (
    <Sentry.ErrorBoundary
      fallback={(props) => <FallbackComponent {...props} />}
      showDialog
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
